/**
 * Email Validation Status Hook (SSE-based)
 *
 * Manages Server-Sent Events connection for real-time email validation status.
 * Connects to the backend SSE endpoint after OTP is sent for unknown domains.
 *
 * Architecture:
 * - Uses EventSource API for SSE connection
 * - Handles connection lifecycle (connect, disconnect, cleanup)
 * - Provides status updates via callbacks and state
 * - Fail-open design: on errors/timeout, proceed without blocking
 *
 * @module features/auth/hooks/useEmailValidationStatus
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { buildApiUrl, userEndpoints } from '@lib/api';
import type { EmailValidationStatus, EmailValidationStatusEvent } from '../services/userAuthApi';

// ─────────────────────────────────────────────────────────────────────────────
// Hook Types
// ─────────────────────────────────────────────────────────────────────────────

export interface UseEmailValidationStatusOptions {
  /** Called when email is validated successfully */
  onValid?: () => void;
  /** Called when email is invalid (hard block) */
  onInvalid?: (reason: string) => void;
  /** Called when validation times out (fail-open) */
  onTimeout?: () => void;
  /** Called on connection error (fail-open) */
  onError?: (error: Error) => void;
}

export interface UseEmailValidationStatusReturn {
  /** Current validation status */
  status: EmailValidationStatus | 'idle' | 'error';
  /** Reason for invalid status */
  invalidReason: string | null;
  /** Whether currently validating */
  isValidating: boolean;
  /** Connect to SSE stream for validation status */
  connect: (jobId: string) => void;
  /** Disconnect from SSE stream */
  disconnect: () => void;
  /** Reset state to idle */
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook for managing email validation status via SSE
 *
 * @example
 * ```tsx
 * const { status, invalidReason, connect, reset } = useEmailValidationStatus({
 *   onInvalid: (reason) => setError(reason),
 * });
 *
 * // After OTP is sent successfully with validationJobId
 * useEffect(() => {
 *   if (otpResult?.validationJobId) {
 *     connect(otpResult.validationJobId);
 *   }
 * }, [otpResult, connect]);
 *
 * // In UI
 * <EmailValidationIndicator status={status} reason={invalidReason} />
 * ```
 */
export function useEmailValidationStatus(
  options: UseEmailValidationStatusOptions = {}
): UseEmailValidationStatusReturn {
  const { onValid, onInvalid, onTimeout, onError } = options;

  // State
  const [status, setStatus] = useState<EmailValidationStatus | 'idle' | 'error'>('idle');
  const [invalidReason, setInvalidReason] = useState<string | null>(null);

  // Refs for EventSource (prevents re-renders and ensures cleanup)
  const eventSourceRef = useRef<EventSource | null>(null);
  const jobIdRef = useRef<string | null>(null);

  /**
   * Clean up EventSource connection
   */
  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    jobIdRef.current = null;
  }, []);

  /**
   * Disconnect from SSE stream
   */
  const disconnect = useCallback(() => {
    cleanup();
  }, [cleanup]);

  /**
   * Reset state to idle
   */
  const reset = useCallback(() => {
    cleanup();
    setStatus('idle');
    setInvalidReason(null);
  }, [cleanup]);

  /**
   * Connect to SSE stream for validation status
   */
  const connect = useCallback((jobId: string) => {
    // Clean up any existing connection
    cleanup();

    // Store job ID for reference
    jobIdRef.current = jobId;

    // Build the SSE URL
    const endpoint = userEndpoints.auth.validationStatus(jobId);
    const url = buildApiUrl(endpoint);

    console.log('🔍 Email Validation: Connecting to SSE', { jobId, url });

    // Set validating state
    setStatus('validating');
    setInvalidReason(null);

    try {
      // Create EventSource with credentials for auth cookies
      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      // Handle incoming messages
      eventSource.onmessage = (event) => {
        try {
          const data: EmailValidationStatusEvent = JSON.parse(event.data);
          console.log('🔍 Email Validation: Received event', data);

          switch (data.status) {
            case 'validating':
              setStatus('validating');
              break;

            case 'valid':
              setStatus('valid');
              cleanup();
              onValid?.();
              break;

            case 'invalid':
              setStatus('invalid');
              setInvalidReason(data.reason || 'Email address cannot receive messages');
              cleanup();
              onInvalid?.(data.reason || 'Email address cannot receive messages');
              break;

            case 'timeout':
              setStatus('timeout');
              cleanup();
              onTimeout?.();
              break;

            default:
              console.warn('🔍 Email Validation: Unknown status', data.status);
          }
        } catch (parseError) {
          console.error('🔍 Email Validation: Failed to parse event', parseError);
        }
      };

      // Handle connection errors (fail-open)
      eventSource.onerror = (event) => {
        console.error('🔍 Email Validation: SSE error', event);

        // Close the connection on error
        cleanup();

        // Fail-open: set status to idle (don't block user)
        setStatus('idle');

        // Notify caller of error
        onError?.(new Error('Connection to validation service failed'));
      };

      // Handle connection open
      eventSource.onopen = () => {
        console.log('🔍 Email Validation: SSE connection opened');
      };

    } catch (err) {
      console.error('🔍 Email Validation: Failed to create EventSource', err);

      // Fail-open: on any error, just reset to idle
      setStatus('idle');
      onError?.(err instanceof Error ? err : new Error('Failed to connect to validation service'));
    }
  }, [cleanup, onValid, onInvalid, onTimeout, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    status,
    invalidReason,
    isValidating: status === 'validating',
    connect,
    disconnect,
    reset,
  };
}

export type { EmailValidationStatus };
